/**
 * SmartChoice Types
 *
 * Centralized type definitions for SmartChoice system
 */

import { CareCategories, ProviderTypes } from '../../common/Constants';

// ============================================================================
// CONTEXT & INPUT TYPES
// ============================================================================

/**
 * Complete context required for SmartChoice eligibility determination
 */
export interface SmartChoiceContext {
  // Category & Search
  chipValue: string;
  category?: string; // Can override chipValue
  searchType?: string;
  searchBySpeciality?: string;
  sectionType?: string;

  // Provider
  providerType?: string;

  // Member Context
  isNiceMember: boolean;

  // Coverage (for widget mode)
  coverageType?: string; // 'M' for Medicare
}

/**
 * Feature flag configuration for SmartChoice
 */
export interface SmartChoiceFlags {
  enablePSXProviderRecommendations: boolean;
  enablePSXFacilitiesRecommendations: boolean;
  isSmartChoiceSuppressed: boolean;
}

// ============================================================================
// RESULT TYPES
// ============================================================================

/**
 * Internal result structure for core eligibility logic
 */
export interface SmartChoiceEligibilityResult {
  isEnabled: boolean;
  isAvailable: boolean;
}

/**
 * Complete eligibility information with diagnostic data
 * Used by 'full' and 'widget' modes
 */
export interface SmartChoiceEligibility {
  // Primary states
  isEnabled: boolean;
  isAvailable: boolean;

  // Diagnostic information
  reason?: SmartChoiceEligibilityReason;
  failedChecks?: string[];

  // Context data
  currentCategory: string;
  isNonSmartChoiceCategory: boolean;
  isEligibleCategory: boolean;
  eligibleCategories?: CareCategories[];

  // Configuration
  flags: SmartChoiceFlags;

  // Provider validation (for advanced use)
  providerTypeValid?: boolean;
  allowedProviderTypes?: ProviderTypes[];

  // Coverage (for widget mode)
  coverageType?: string;
  coverageAllowsSmartChoice?: boolean;
}

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

/**
 * Detailed reason codes for eligibility decisions
 */
export enum SmartChoiceEligibilityReason {
  // Enabled
  ENABLED = 'ENABLED',

  // Global disqualifiers (Layer 1)
  NICE_MEMBER = 'NICE_MEMBER',
  SUPPRESSED = 'SUPPRESSED',
  FLAGS_DISABLED = 'FLAGS_DISABLED',

  // Category disqualifiers (Layer 2)
  INELIGIBLE_CATEGORY = 'INELIGIBLE_CATEGORY',
  INVALID_PROVIDER_TYPE = 'INVALID_PROVIDER_TYPE',
  COVERAGE_NOT_ALLOWED = 'COVERAGE_NOT_ALLOWED',
  NON_SMART_CHOICE_CATEGORY = 'NON_SMART_CHOICE_CATEGORY',

  // Context disqualifiers (Layer 3)
  NAME_SEARCH = 'NAME_SEARCH',
  NON_SPECIALTY_SEARCH = 'NON_SPECIALTY_SEARCH',
  GROUP_SECTION = 'GROUP_SECTION',
  FACILITY_SECTION_MISMATCH = 'FACILITY_SECTION_MISMATCH',
}

/**
 * SINGLE SOURCE OF TRUTH for SmartChoice category configuration
 *
 * All SmartChoice rules for each category are defined here in one place.
 * To enable/disable SmartChoice for a category, just add/remove/modify one entry.
 *
 * @example
 * // To enable SmartChoice for a new category:
 * [CareCategories.DENTAL]: {
 *   enabled: true,
 *   featureFlag: 'enablePSXProviderRecommendations',
 *   providerTypes: [ProviderTypes.PRACTITIONER],
 *   coverageTypes: ['D'],
 * }
 */
export const SMART_CHOICE_CATEGORY_CONFIG: Readonly<
  Partial<
    Record<
      CareCategories,
      {
        readonly enabled: boolean;
        readonly featureFlag: keyof SmartChoiceFlags;
        readonly providerTypes: readonly ProviderTypes[];
        readonly coverageTypes: readonly string[];
      }
    >
  >
> = {
  [CareCategories.PRIMARY_CARE]: {
    enabled: true,
    featureFlag: 'enablePSXProviderRecommendations',
    providerTypes: [ProviderTypes.PRACTITIONER],
    coverageTypes: ['M'],
  },
  [CareCategories.MEDICAL_SPECIALISTS]: {
    enabled: true,
    featureFlag: 'enablePSXProviderRecommendations',
    providerTypes: [ProviderTypes.PRACTITIONER],
    coverageTypes: ['M'],
  },
  [CareCategories.FACILITIES]: {
    enabled: true,
    featureFlag: 'enablePSXFacilitiesRecommendations',
    providerTypes: [ProviderTypes.ORGANIZATION, ProviderTypes.PRACTITIONER],
    coverageTypes: ['M'],
  },
} as const;

/**
 * Categories that NEVER support SmartChoice
 *
 * Automatically derived from SMART_CHOICE_CATEGORY_CONFIG.
 * Categories not listed there or with enabled=false are excluded.
 */
export const NON_SMART_CHOICE_CATEGORIES: readonly CareCategories[] = [
  CareCategories.BEHAVIORAL_HEALTH,
  CareCategories.DENTAL,
  CareCategories.VISION,
  CareCategories.HEALTH_SUPPLIES,
  // Categories not in SMART_CHOICE_CATEGORY_CONFIG or with enabled=false
] as const;

/**
 * Feature flag to category mapping
 * Automatically derived from SMART_CHOICE_CATEGORY_CONFIG
 */
export const FEATURE_FLAG_CATEGORY_MAP: Readonly<{
  enablePSXProviderRecommendations: readonly CareCategories[];
  enablePSXFacilitiesRecommendations: readonly CareCategories[];
}> = {
  enablePSXProviderRecommendations: Object.entries(SMART_CHOICE_CATEGORY_CONFIG)
    .filter(
      ([_, config]) =>
        config.enabled &&
        config.featureFlag === 'enablePSXProviderRecommendations'
    )
    .map(([cat, _]) => cat as CareCategories),
  enablePSXFacilitiesRecommendations: Object.entries(
    SMART_CHOICE_CATEGORY_CONFIG
  )
    .filter(
      ([_, config]) =>
        config.enabled &&
        config.featureFlag === 'enablePSXFacilitiesRecommendations'
    )
    .map(([cat, _]) => cat as CareCategories),
} as const;

/**
 * Provider type requirements per category
 * Automatically derived from SMART_CHOICE_CATEGORY_CONFIG
 */
export const PROVIDER_TYPE_RULES: Readonly<
  Partial<Record<CareCategories, readonly ProviderTypes[]>>
> = Object.fromEntries(
  Object.entries(SMART_CHOICE_CATEGORY_CONFIG)
    .filter(([_, config]) => config.enabled)
    .map(([cat, config]) => [cat, config.providerTypes])
) as Readonly<Partial<Record<CareCategories, readonly ProviderTypes[]>>>;

/**
 * Coverage type requirements per category
 * Automatically derived from SMART_CHOICE_CATEGORY_CONFIG
 *
 * Current rules:
 * - Primary Care: Medicare ('M') only
 * - Medical Specialists: Medicare ('M') only
 * - Facilities: Medicare ('M') only
 * - Behavioral Health: Behavioral coverage ('B') only
 */
export const COVERAGE_TYPE_RULES: Readonly<
  Partial<Record<CareCategories, readonly string[]>>
> = Object.fromEntries(
  Object.entries(SMART_CHOICE_CATEGORY_CONFIG)
    .filter(([_, config]) => config.enabled)
    .map(([cat, config]) => [cat, config.coverageTypes])
) as Readonly<Partial<Record<CareCategories, readonly string[]>>>;
